import { get } from 'lodash';

import { formatCapacityForDisplay } from '@/delivery-type-plugin/components/quantityUnit';
import { formatVendorDeviceCellLabel } from '@/delivery-type-plugin/components/vendorAndDeviceRadioList';

import type { EbmResourceConfigurationRecord } from '../types';

function readPlainText(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return text && text !== '[object Object]' ? text : undefined;
  }
  return undefined;
}

/**
 * 容量展示文案：兼容 string，以及 `{ value, unit }` / `{ default }` / `{ size }` 等对象结构。
 * 仅用于 UI；提交侧对 NVMe capacity 保持原样，勿经此函数格式化。
 */
export function formatEbmCapacityText(capacity: unknown): string {
  const direct = readPlainText(capacity);
  if (direct) return formatCapacityForDisplay(direct) || direct;
  if (!capacity || typeof capacity !== 'object' || Array.isArray(capacity)) return '';

  const record = capacity as Record<string, unknown>;
  const unitRaw = record.unit;
  const unitRecord = unitRaw && typeof unitRaw === 'object' && !Array.isArray(unitRaw) ? (unitRaw as Record<string, unknown>) : undefined;
  const unit = readPlainText(unitRaw) || (unitRecord ? readPlainText(unitRecord.default) : undefined);
  const supported = unitRecord?.supported;

  const value =
    readPlainText(record.value) ||
    readPlainText(record.default) ||
    readPlainText(record.size) ||
    readPlainText(record.capacity) ||
    readPlainText(record.amount) ||
    readPlainText(record.total);

  if (!value) return '';
  if (/[a-zA-Z]+$/.test(value)) return formatCapacityForDisplay(value, undefined, supported) || value;
  if (!unit) return value;
  return formatCapacityForDisplay(value, unit, supported) || `${value}${unit}`;
}

/** 格式化 CPU / GPU 等设备规格文案 */
export function formatEbmDeviceText(v: Record<string, unknown> | undefined): string {
  if (!v) return '';
  const number = get(v, 'resource.size');
  if (!number) return '';
  const device = v.device ? formatVendorDeviceCellLabel(String(v.device)) : '-';
  const vendor = v.vendor ? ` · ${formatVendorDeviceCellLabel(String(v.vendor))}` : '';
  return `${device} * ${number}${vendor}`;
}

/** 格式化内存规格文案（容量可升单位展示，提交仍用原始 resource.size） */
export function formatEbmMemoryText(v: Record<string, unknown> | undefined): string {
  if (!v || !get(v, 'resource.size')) return '';
  const rawSize = get(v, 'resource.size');
  const displaySize = formatCapacityForDisplay(rawSize) || String(rawSize);
  return `${v.deviceType || '-'} * ${displaySize}`;
}

/** 格式化单条存储 / 网卡规格文案 */
export function formatEbmSpecItemText(i: Record<string, unknown>): string {
  const linkSpeed = i?.linkSpeed ? `${String(i.linkSpeed)} · ` : '';
  const number = get(i, 'resource.size');
  const device = i?.device ? formatVendorDeviceCellLabel(String(i.device)) : '-';
  const base = number ? `${device} * ${number}` : device;
  const capacityText = formatEbmCapacityText(i?.capacity);
  const capacity = capacityText ? ` · ${capacityText}` : '';
  const vendor = i?.vendor ? ` · ${formatVendorDeviceCellLabel(String(i.vendor))}` : '';
  return `${linkSpeed}${base}${capacity}${vendor}`;
}

/** 将存储 / 网卡列表拼成展示文案 */
export function formatEbmSpecListText(arr: Record<string, unknown>[] | undefined): string {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  return arr.map(formatEbmSpecItemText).join(' / ');
}

export interface EbmCompositionPreviewRow {
  /** displayConfig / descriptor id */
  id: string;
  /** 展示标签 */
  label: string;
  /** 展示值（空串表示跳过） */
  value: string;
}

/** 从 resourceConfiguration composition 结构生成预览行 */
export function buildEbmCompositionPreviewRows(bundle: EbmResourceConfigurationRecord | undefined): EbmCompositionPreviewRow[] {
  if (!bundle) return [];

  const rows: EbmCompositionPreviewRow[] = [
    { id: 'cpu', label: 'CPU', value: formatEbmDeviceText(bundle.cpu) },
    { id: 'memory', label: '内存', value: formatEbmMemoryText(bundle.memory) },
    { id: 'gpu', label: 'GPU', value: formatEbmDeviceText(bundle.gpu) },
    { id: 'storage', label: '存储', value: formatEbmSpecListText(bundle.storage) },
    { id: 'nic', label: '网卡', value: formatEbmSpecListText(bundle.nic) },
  ];

  return rows.filter((row) => Boolean(row.value?.trim()));
}
